// kernel/useAnalytics.tsx
import React, { createContext, useContext, ReactNode, useMemo } from 'react';

// Define the shape of the analytics context
export interface AnalyticsContextProps {
  track: (eventName: string, properties?: object) => void;
  peopleSet: (properties: object) => void;
  reset: () => void;
  register: (properties: object) => void;
  peopleSetOnce: (properties: object) => void;
}

// Create the context with a placeholder default value
const AnalyticsContext = createContext<AnalyticsContextProps | undefined>(undefined);

// Define props for the provider
interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const noop = useMemo(() => {
    return {
      track: () => {},
      peopleSet: () => {},
      reset: () => {},
      register: () => {},
      peopleSetOnce: () => {},
    };
  }, []);

  return (
    <AnalyticsContext.Provider value={noop}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom hook to consume the analytics context
export const useAnalytics = (): AnalyticsContextProps => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    // This error means a component tried to use the hook outside of the provider tree
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
